import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, getSession } from "../api/webcheckinApi";
import { useWizard, type Step } from "../state/wizard";
import { WizardContext } from "./shared";
import { Spinner } from "../components/Spinner";
import { Stepper } from "../components/Stepper";
import { WelcomeScreen } from "./WelcomeScreen";
import { ConfirmContactScreen } from "./ConfirmContactScreen";
import { RegisterScreen } from "./RegisterScreen";
import { CompanionsScreen } from "./CompanionsScreen";
import { ConsentScreen } from "./ConsentScreen";
import { EtaSubmitScreen } from "./EtaSubmitScreen";
import { SuccessScreen } from "./SuccessScreen";
import { ArrivalScreen } from "./ArrivalScreen";

export function Wizard() {
  const { token } = useParams<{ workspace: string; token: string }>();
  const { state, dispatch } = useWizard();

  const q = useQuery({
    queryKey: ["session", token],
    queryFn: () => getSession(token!),
    enabled: !!token,
  });

  useEffect(() => {
    if (q.data && token) dispatch({ type: "hydrate", payload: q.data, token });
  }, [q.data, dispatch, token]);

  // Tab title tracks the property name so the guest sees which hotel they're
  // checking into if they have multiple tabs open. Reset on unmount so any
  // subsequent route (e.g. the BlankNotFound landing) can override it.
  const propertyName = q.data?.property.name ?? null;
  useEffect(() => {
    document.title = propertyName ? `${propertyName} — Online check-in` : "Online check-in";
    return () => { document.title = "Online check-in"; };
  }, [propertyName]);

  if (!token) {
    return (
      <Shell>
        <h1 className="screen-h1">Invalid link</h1>
      </Shell>
    );
  }
  if (q.isLoading) return <Shell><Spinner label="Loading your reservation…" /></Shell>;
  if (q.isError) {
    const isExpired = q.error instanceof ApiError && q.error.isExpired;
    return (
      <Shell>
        <h1 className="screen-h1">{isExpired ? "Link no longer valid" : "Something went wrong"}</h1>
        <p className="screen-p mt-2">
          {isExpired
            ? "This check-in link has expired or been cancelled. Please contact the property directly."
            : "We couldn't load your reservation. Please refresh, or try again in a moment."}
        </p>
        {!isExpired && (
          <button className="btn-primary mt-6" onClick={() => q.refetch()}>
            Try again
          </button>
        )}
      </Shell>
    );
  }
  if (!state.payload) return <Shell><Spinner /></Shell>;

  const showStepper = stepShowsStepper(state.step);

  return (
    <WizardContext.Provider value={{ state, dispatch }}>
      <Shell>
        <Header property={state.payload.property.name} />
        {showStepper && <Stepper current={state.step} />}
        <ScreenRouter step={state.step} token={token} />
      </Shell>
    </WizardContext.Provider>
  );
}

/**
 * Standard wrapper for every wizard state (loading / error / active / invalid).
 * Guarantees the "Powered by Polynoos" footer appears on ALL guest-facing
 * pages, keeping the branding + link consistent regardless of what the inner
 * content is doing.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    // screen-shell already sets `flex min-h-full flex-col` — flex-1 on the
    // inner wrapper keeps the footer pinned at the bottom on short pages.
    <div className="screen-shell">
      <div className="flex-1">{children}</div>
      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer className="mt-8 pt-4 border-t border-slate-100 text-center text-[11px] text-slate-400">
      Powered by{" "}
      <a
        href="https://polynoos.com"
        target="_blank"
        rel="noopener noreferrer"
        className="text-slate-500 hover:text-slate-700 underline underline-offset-2"
      >
        Polynoos Hospitality Platform
      </a>
    </footer>
  );
}

function stepShowsStepper(step: Step): boolean {
  // The confirmContact step is preliminary — it precedes even Welcome and
  // isn't one of the "check-in" steps the guest needs progress feedback for.
  return !(step === "success" || step === "arrival" || step === "submitting" || step === "confirmContact");
}

function Header({ property }: { property: string }) {
  return (
    <div className="screen-header flex items-center justify-between border-b border-slate-200 pb-3">
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Check-in</div>
        <div className="text-base font-semibold text-slate-900">{property}</div>
      </div>
      <div className="h-9 w-9 rounded-lg bg-brand text-white flex items-center justify-center font-bold">P</div>
    </div>
  );
}

function ScreenRouter({ step, token }: { step: Step; token: string }) {
  switch (step) {
    case "welcome":        return <WelcomeScreen />;
    case "confirmContact": return <ConfirmContactScreen token={token} />;
    case "register":   return <RegisterScreen token={token} />;
    case "companions": return <CompanionsScreen token={token} />;
    case "consent":    return <ConsentScreen token={token} />;
    case "eta":        return <EtaSubmitScreen token={token} />;
    case "submitting": return <Spinner label="Submitting your check-in…" />;
    case "success":    return <SuccessScreen />;
    case "arrival":    return <ArrivalScreen />;
  }
}
